import { Col, Row } from "react-bootstrap";
import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useGet, usePost } from "@/apiHooks/HttpFunctions";

import BasicInput from "@/components/BasicInput";
import BasicModal from "@/Modals/BasicModal";
import BrandSchema from "@/schema/WebDataManagement/MasterData/Brands/BrandSchema";
import { EditBrandInitialValues } from "@/data/WebDataManagement/MasterData/Brands/initial-values";
import ImageInput from "@/components/ImageInput/Index";
import RenderToast from "@/components/RenderToast";
import { brandsMasterDataApi } from "@/apis/apis";
import useCloseModalOnNotFound from "@/CustomHooks/useCloseModalOnNotFound";
import useFormApiState from "@/CustomHooks/useFormApiState";
import { useFormik } from "formik";
import useFormikErrorToast from "@/CustomHooks/useFormikErrorToast";
import useHttpErrorToast from "@/CustomHooks/useHttpErrorToast";

const BrandModal = forwardRef((_, ref) => {
  const [modalState, setModalState] = useState({ isOpen: false, id: null });

  const showApi = modalState.id
    ? `${brandsMasterDataApi}/${modalState.id}`
    : null;

  const { data: showData, error: showError } = useGet({
    apiURL: showApi,
    enabled: !!modalState.id,
  });

  const handleSuccess = (action) => (data) => {
    closeModal();
    RenderToast({
      type: "success",
      message: `Brand ${action} successfully.`,
    });
  };
  const {
    mutate: saveEdit,
    isPending: saveEditIsPending,
    error: saveEditError,
  } = usePost({
    apiURL: `${brandsMasterDataApi}/${modalState.id}`,
    isFormData: true,
    onSuccess: handleSuccess("edited"),
  });
  const {
    mutate: saveCreate,
    isPending: saveCreateIsPending,
    error: saveCreateError,
  } = usePost({
    apiURL: brandsMasterDataApi,
    isFormData: true,
    onSuccess: handleSuccess("added"),
  });
  const { saveIsPending, saveError, resetError } = useFormApiState(
    [saveEditIsPending, saveCreateIsPending],
    [saveEditError, saveCreateError]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: EditBrandInitialValues(modalState.id ? showData : null),
    validationSchema: BrandSchema(!!modalState.id),
    onSubmit: (values) => {
      handleSubmit(values);
    },
  });

  const handleSubmit = (values) => {
    const formData = new FormData();
    if (modalState.id) formData.append("_method", "PUT");
    if (values.icon) formData.append("icon", values.icon);
    formData.append("code", values.code);
    formData.append("name", values.name);

    modalState.id ? saveEdit(formData) : saveCreate(formData);
  };

  useImperativeHandle(ref, () => ({
    open: (props) => setModalState({ isOpen: true, id: props?.id }),
    close: closeModal,
  }));

  const closeModal = useCallback(() => {
    formik.resetForm();
    resetError();
    setModalState({ isOpen: false, id: null });
  }, [resetError, formik]);

  useCloseModalOnNotFound(showError, closeModal);
  useFormikErrorToast(formik);
  useHttpErrorToast(saveError);

  return (
    <div>
      <BasicModal
        header={modalState.id ? "Edit Brand" : "Add Brand"}
        onHide={closeModal}
        show={modalState.isOpen}
        saveLoading={saveIsPending}
        formik={formik}
      >
        <Row>
          <Col md={12}>
            <BasicInput
              formik={formik}
              fieldName={"code"}
              saveError={saveError}
              label="ID"
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <BasicInput
              formik={formik}
              fieldName={"name"}
              saveError={saveError}
              label="Brand"
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <ImageInput
              formFormik={formik}
              fieldName="icon"
              label="Icon"
              error={saveError}
              existingImageUrl={modalState.id && showData?.data?.icon_url}
            />
          </Col>
        </Row>
      </BasicModal>
    </div>
  );
});

export default BrandModal;
